import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, qs } from '../lib/api';
import { useDebounced } from '../lib/hooks';
import { Avatar } from './People';
import { Icon } from './Icon';

/**
 * اختيار السجل الذي يُربط به النشاط.
 *
 * النشاط لا يقوم بذاته: الخادم يشترط ربطه بعميل أو عميل محتمل أو صفقة، وهو
 * شرطٌ صحيح — نشاطٌ بلا سجل لا يظهر في تاريخ أحد ولا يُعثر عليه بعد أسبوع.
 *
 * ولذلك كان النشاط يُنشأ من داخل الملف وحده. وهذا المُنتقي يفكّ القيد بلا
 * كسر القاعدة: يبدأ من الاسم الذي يذكره المستخدم، ويصل إلى المعرّف الذي
 * يشترطه الخادم.
 */

export interface RecordLink {
  leadId?: string;
  accountId?: string;
  contactId?: string;
  dealId?: string;
}

interface Row {
  id: string;
  name: string;
  company?: string | null;
}

type Kind = 'account' | 'lead';

/**
 * اختيار عميلٍ قائم فقط — بلا تبويب «عميل محتمل».
 *
 * شاشات التنفيذ (المشاريع والانضمام) تخصّ من صار عميلًا فعلًا؛ عرضُ
 * المحتملين فيها يوحي بأن العمل يبدأ قبل التحويل، وهو عكس الحوكمة.
 */
export function AccountPicker({
  onPick,
  label = 'ابحث عن العميل',
}: {
  onPick: (id: string, name: string) => void;
  label?: string;
}) {
  const [q, setQ] = useState('');
  const term = useDebounced(q);

  const results = useQuery({
    queryKey: ['account-picker', term],
    queryFn: () => api<{ items: Row[] }>(`/crm/accounts${qs({ q: term, pageSize: 8 })}`),
    enabled: term.trim().length >= 2,
  });

  const rows = results.data?.items ?? [];

  return (
    <div>
      <div className="field">
        <label className="label" htmlFor="ap-q">
          {label}
        </label>
        <input
          id="ap-q"
          className="input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="اكتب حرفين على الأقل"
          autoComplete="off"
          autoFocus
        />
      </div>

      {term.trim().length < 2 ? (
        <p className="tiny mute mt-2">اكتب جزءًا من اسم العميل للبحث.</p>
      ) : results.isPending ? (
        <div className="skeleton mt-2" style={{ height: 120 }} />
      ) : rows.length === 0 ? (
        <p className="tiny mute mt-2">لا عملاء يطابقون البحث.</p>
      ) : (
        <div className="pick-list mt-2">
          {rows.map((r) => (
            <button
              key={r.id}
              type="button"
              className="pick-row"
              onClick={() => onPick(r.id, r.name)}
            >
              <Avatar name={r.name} size={28} />
              <span className="pick-row-text">
                <strong>{r.name}</strong>
                {r.company && <small>{r.company}</small>}
              </span>
              <Icon name="chevronLeft" size={15} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function RecordPicker({
  onPick,
  label = 'ابحث عن العميل أو العميل المحتمل',
}: {
  onPick: (link: RecordLink, name: string) => void;
  label?: string;
}) {
  const [kind, setKind] = useState<Kind>('account');
  const [q, setQ] = useState('');
  const term = useDebounced(q);

  const results = useQuery({
    queryKey: ['record-picker', kind, term],
    /*
     * لا بحث قبل حرفين.
     *
     * استعلامٌ فارغ يُعيد أول خمسة سجلات بترتيب لا معنى له للباحث، فيظنّها
     * نتائج بحثه ويحتار لماذا لا يجد من يريد.
     */
    queryFn: () =>
      api<{ items: Row[] }>(
        kind === 'account'
          ? `/crm/accounts${qs({ q: term, pageSize: 8 })}`
          : `/crm/leads${qs({ q: term, pageSize: 8 })}`,
      ),
    enabled: term.trim().length >= 2,
  });

  const rows = results.data?.items ?? [];

  return (
    <div>
      <div className="tabs" role="tablist" aria-label="نوع السجل">
        <button
          type="button"
          role="tab"
          aria-selected={kind === 'account'}
          className="tab"
          onClick={() => setKind('account')}
        >
          <Icon name="building" size={14} />
          عميل
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={kind === 'lead'}
          className="tab"
          onClick={() => setKind('lead')}
        >
          <Icon name="funnel" size={14} />
          عميل محتمل
        </button>
      </div>

      <div className="field mt-4">
        <label className="label" htmlFor="rp-q">
          {label}
        </label>
        <input
          id="rp-q"
          className="input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="اكتب حرفين على الأقل"
          autoComplete="off"
          autoFocus
        />
      </div>

      {term.trim().length < 2 ? (
        <p className="tiny mute mt-2">اكتب جزءًا من الاسم للبحث.</p>
      ) : results.isPending ? (
        <div className="skeleton mt-2" style={{ height: 120 }} />
      ) : rows.length === 0 ? (
        <p className="tiny mute mt-2">لا نتائج مطابقة.</p>
      ) : (
        <div className="pick-list mt-2">
          {rows.map((r) => (
            <button
              key={r.id}
              type="button"
              className="pick-row"
              onClick={() =>
                onPick(kind === 'account' ? { accountId: r.id } : { leadId: r.id }, r.name)
              }
            >
              <Avatar name={r.name} size={28} />
              <span className="pick-row-text">
                <strong>{r.name}</strong>
                {r.company && <small>{r.company}</small>}
              </span>
              <Icon name="chevronLeft" size={15} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
